import React, { useState, useEffect } from 'react'
import Modal from '../../../components/Modal'
import Typography from '@mui/material/Typography'
import LanguageStrings from '../../../i18n/locales'
import { CircularProgress, Grid } from '@mui/material'
import { type OperationPermissionState } from '../../dashboard/redux/operationPermissionSlice'
import { useSelector, connect, type ConnectedProps } from 'react-redux'
import { type RootState } from '../../../store'
import { useAuth } from 'react-oidc-context'
import { getOperationPermissions } from '../../dashboard/redux/actionCreator'
import { fetchEmailGroupDetails, deleteEmailGroup } from '../redux/actionCreators'
import { emailGroupDetailsResetAction } from '../redux/emailGroupDetailsSlice'
import { type EmailGroupDetailsState } from '../redux/emailGroupDetailsSlice'
import CustomToast from './ToastComponent'
import EmailAlertModal from './EmailAlertModal'

const emailNotificationStrings = LanguageStrings().ThermalIQStrings.EmailNotificationComponent

const commonStyle = {
  fontFamily: 'Honeywell Sans Web',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.5px',
  textAlign: 'center',
  cursor: 'pointer',
  color: '#64C3FF'
}

interface ActionComponentProps {
  row: any
  fetchAllGroups: () => void
  isGroupSelected: boolean
  setIsGroupSelected: () => void
}

const ActionComponent = (props: Props): JSX.Element => {
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [isLoader, setIsLoader] = useState(false)
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()

  useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  useEffect(() => {
    if (showViewModal || props.isGroupSelected) {
      props.fetchEmailGroupDetails(props.row.groupId)
    }
  }, [showViewModal, props.isGroupSelected])

  useEffect(() => {
    if (showViewModal && props.emailGroupDetails.loadingError !== null && props.emailGroupDetails.loadingError !== undefined) {
      setShowViewModal(false)
      CustomToast({
        type: 'error',
        heading: 'Error',
        /* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */
        message: props.emailGroupDetails.loadingError || 'Failed to load group details'
      })
    }
  }, [showViewModal, props.emailGroupDetails.loadingError])

  const handleEditClick = (): void => {
    setShowUpdateModal(true)
  }

  const closeUpdateModal = (): void => {
    setShowUpdateModal(false)
    props.fetchAllGroups() // Refresh the list after update
  }

  const handleViewClick = (): void => {
    props.resetEmailGroupDetailsError()
    setShowViewModal(true)
  }
  const closeViewModal = (): void => {
    props.setIsGroupSelected()
    setShowViewModal(false)
  }

  const handleDeleteClick = (): void => {
    setShowDeleteModal(true)
  }
  const handleDelete = (): void => {
    setShowDeleteSuccessModal(false)
    setShowDeleteModal(false)
    props.fetchAllGroups()
  }
  const closeDeleteModal = (): void => {
    setShowDeleteModal(false)
  }

  const handleDeleteConfirm = (): void => {
    setIsLoader(true)
    props.deleteEmailGroup(
      props.row.groupId,
      (_response, _httpStatus) => {
        setIsLoader(false)
        setShowDeleteModal(false)
        setShowDeleteSuccessModal(true)
        CustomToast({
          type: 'success',
          heading: 'Success',
          message: emailNotificationStrings.DeleteSuccessMessage
        })
        props.fetchAllGroups()
      },
      (errorMessage, _httpStatus) => {
        setIsLoader(false)
        setShowDeleteModal(false)
        CustomToast({
          type: 'error',
          heading: 'Error',
          /* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */
          message: errorMessage || 'Failed to delete group'
        })
      }
    )
  }

  const handleDeleteWithFailure = (): JSX.Element => {
    return (
      <Modal title={'Loading...'}>
        <div className="CircularprogressAmi">
          <CircularProgress />
        </div>
      </Modal>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', height: '39px', padding: '0px 15px 0px 0px', alignItems: 'center', gap: '15px' }}>
        {
          operationPermissions.permissions.can_read_tiq_communications_service
            ? <Typography sx={commonStyle} onClick={handleViewClick}>{emailNotificationStrings.View}</Typography>
            : <></>
        }
        {
          operationPermissions.permissions.can_modify_tiq_communications_service
            ? <Typography sx={commonStyle} onClick={handleEditClick}>
                {emailNotificationStrings.Edit}
              </Typography>
            : <></>
        }
        {
          operationPermissions.permissions.can_delete_tiq_communications_service
            ? <Typography sx={commonStyle} onClick={handleDeleteClick}>{emailNotificationStrings.Delete}</Typography>
            : <></>
        }
      </div>
      {(showViewModal || props.isGroupSelected)
        ? <Modal
            title={emailNotificationStrings.ViewGroupDetails}
            onClose={closeViewModal}
            sx={{
              width: {
                xs: '100%',
                sm: '550px'
              },
              height: {
                xs: '100%',
                sm: '350px'
              }
            }}
            LabelNegative='Close'
            onClickNegative={closeViewModal}
          >
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: props.emailGroupDetails.isLoading ? 'center' : 'flex-start' }}>
              {props.emailGroupDetails.isLoading
                ? (
                    <div className="CircularprogressAmi" style={{ display: 'flex', minHeight: '200px', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </div>
                  )
                /* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */
                : props.emailGroupDetails.groupDetails
                  ? (
                      <Grid container spacing={3} style={{ color: '#fff', fontFamily: 'Honeywell Sans Web', fontSize: '18px', marginTop: '20px', paddingLeft: '10px' }} justifyContent={'center'} display={'block'} >
                        <Grid item display={'flex'} sm={12}>
                          <Grid item sm={3} > <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#F0F0F0' }}>{emailNotificationStrings.GroupName}</Typography> </Grid>
                          <Grid item sm={7} > <Typography sx={{ fontWeight: 500, color: '#D0D0D0' }}> : {props.emailGroupDetails.groupDetails.groupName}</Typography> </Grid>
                        </Grid>
                        <Grid item display={'flex'} sm={12}>
                          <Grid item sm={3} ><Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#F0F0F0' }}>{emailNotificationStrings.SiteName}</Typography></Grid>
                          <Grid item sm={7} ><Typography sx={{ fontWeight: 500, color: '#D0D0D0' }}>
                             : {Object.values(props.emailGroupDetails.groupDetails.siteDetails).join(', ')}
                          </Typography>
                          </Grid>
                        </Grid>
                        <Grid item display={'flex'} sm={12} >
                          <Grid item sm={3} ><Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#F0F0F0' }}>{emailNotificationStrings.AlarmSeverity}</Typography></Grid>
                          <Grid item sm={7} >
                            <Typography sx={{ fontWeight: 500, color: '#D0D0D0' }}>
                             : {Object.values(props.emailGroupDetails.groupDetails.severityDetails).join(', ')}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item display={'flex'} sm={12} >
                          <Grid item sm={3} > <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#F0F0F0' }}>{emailNotificationStrings.DistributionList}</Typography></Grid>
                          <Grid item sm={9} >
                            <Typography sx={{ fontWeight: 500, color: '#D0D0D0' }}>
                             : {props.emailGroupDetails.groupDetails.distributionList.join(', ')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )
                  : (
                    <div style={{ color: '#fff', fontFamily: 'Honeywell Sans Web', fontSize: '16px', padding: '20px', height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {props.emailGroupDetails.loadingError ?? 'Failed to load group details'}
                    </div>
                    )}
            </div>
          </Modal>
        : null
      }
      {showDeleteModal
        ? <Modal
            title={`Delete Group ${typeof (props.row as { groupName: string })?.groupName === 'string' && (props.row as { groupName: string }).groupName.length > 15 ? `${(props.row as { groupName: string }).groupName.slice(0, 15)}...` : typeof (props.row as { groupName: string })?.groupName === 'string' ? (props.row as { groupName: string }).groupName : ''}`}
            onClose={closeDeleteModal}
            width={'600px'}
            height={'292px'}
            buttonTypeNegative='hollow'
            LabelNegative='Cancel'
            LabelPositive='Delete'
            onClickPositive={handleDeleteConfirm}
            onClickNegative={closeDeleteModal}
          >
            <div style={{ margin: '20px 0 10px 0' }}>
              <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', lineHeight: '24px' }}>
                Are you sure you want to delete this email group?
                <br/><br/>
                Deleting the email group will remove all associated notification settings. This action cannot be undone.
              </Typography>
            </div>
          </Modal>
        : null
      }
      { isLoader && handleDeleteWithFailure()}
      {showDeleteSuccessModal && (
        <Modal
          title={'Success'}
          onClose={() => { handleDelete() }}
          width={'450px'}
          height={'auto'}
          LabelPositive={'OK'}
          buttonTypePositive={'filled'}
        >
          <Typography sx={{ mt: 2, mb: 2, fontFamily: 'Honeywell Sans Web' }}>
            {emailNotificationStrings.DeleteSuccessMessage}
          </Typography>
        </Modal>
      )}
      {showUpdateModal && (
        <EmailAlertModal
          open={showUpdateModal}
          onClose={closeUpdateModal}
          groupId={props.row.groupId}
          mode="update"
        />
      )}
    </>
  )
}

interface DispatchToProps {
  getOperationPermissions: (sub: string) => void
  fetchEmailGroupDetails: (groupId: string) => void
  deleteEmailGroup: (groupId: string, onSuccess: (response: any, httpStatus: number) => void, onFailure: (errorMessage: string, httpStatus: number) => void) => void
  resetEmailGroupDetailsError: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub)),
  fetchEmailGroupDetails: (groupId: string) => dispatch(fetchEmailGroupDetails(groupId)),
  deleteEmailGroup: (groupId: string, onSuccess: (response: any, httpStatus: number) => void, onFailure: (errorMessage: string, httpStatus: number) => void) => dispatch(deleteEmailGroup(groupId, onSuccess, onFailure)),
  resetEmailGroupDetailsError: () => dispatch(emailGroupDetailsResetAction())
})

interface StateProps {
  emailGroupDetails: EmailGroupDetailsState
}

const mapStateToProps = (state: RootState): StateProps => ({
  emailGroupDetails: state.emailGroupDetails
})

const connector = connect(mapStateToProps, mapDispatchToProps)

type ReduxProps = ConnectedProps<typeof connector>
type Props = ActionComponentProps & ReduxProps

export default connector(ActionComponent)
